import type { FinanceInvoice } from "@/lib/financeTypes";
import { computeLineAmounts } from "@/lib/financeInvoiceLineMath";

function roundInvoiceMoney(value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export type FinanceInvoiceTotals = {
  subtotal: number;
  discountAmount: number;
  discountLabel: string;
  taxAmount: number;
  taxRate: number;
  shippingCharges: number;
  lateFee: number;
  total: number;
};

export function financeInvoiceTotals(invoice: FinanceInvoice): FinanceInvoiceTotals {
  const lines = invoice.lines ?? [];
  let subtotal = 0;
  if (lines.length > 0) {
    subtotal = roundInvoiceMoney(
      lines.reduce((sum, line) => {
        const computed = computeLineAmounts(line);
        const amount =
          line.amount != null && Number.isFinite(Number(line.amount))
            ? Number(line.amount)
            : computed.amount;
        return sum + amount;
      }, 0),
    );
  } else {
    subtotal = roundInvoiceMoney(Number(invoice.amount) || 0);
  }

  const discountAmount = roundInvoiceMoney(Math.max(0, Number(invoice.discountAmount) || 0));
  const cappedDiscount = discountAmount > subtotal ? subtotal : discountAmount;
  const taxable = Math.max(0, roundInvoiceMoney(subtotal - cappedDiscount));
  const taxRate = Math.max(0, Number(invoice.taxRate) || 0);
  const lineTax = roundInvoiceMoney(
    lines.reduce((sum, line) => sum + computeLineAmounts(line).taxAmount, 0),
  );
  const taxAmount = lineTax > 0 ? lineTax : roundInvoiceMoney(taxable * (taxRate / 100));
  const shippingCharges = roundInvoiceMoney(Math.max(0, Number(invoice.shippingCharges) || 0));
  const lateFee = roundInvoiceMoney(Math.max(0, Number(invoice.lateFee) || 0));
  const total = roundInvoiceMoney(taxable + taxAmount + shippingCharges + lateFee);

  return {
    subtotal,
    discountAmount: cappedDiscount,
    discountLabel: (invoice.discountLabel || "").trim() || "Discount",
    taxAmount,
    taxRate: lineTax > 0 && taxable > 0 ? roundInvoiceMoney((taxAmount / taxable) * 100) : taxRate,
    shippingCharges,
    lateFee,
    total: total > 0 ? total : subtotal,
  };
}

export function financeInvoiceBalanceDue(invoice: FinanceInvoice): number {
  const totals = financeInvoiceTotals(invoice);
  const due = totals.total || Number(invoice.amount) || 0;
  return Math.max(0, roundInvoiceMoney(due - (Number(invoice.amountPaid) || 0)));
}
